import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";

import { api, ApiError } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { useQueryClient } from "@tanstack/react-query";
import type { Organization } from "@/types";

const SUPPORTED_PLAN_IDS = ["STARTER", "PROFESSIONAL", "ENTERPRISE"] as const;

type SupportedPlanId = (typeof SUPPORTED_PLAN_IDS)[number];

function isSupportedPlanId(value: unknown): value is SupportedPlanId {
  return (
    typeof value === "string" &&
    SUPPORTED_PLAN_IDS.includes(value as SupportedPlanId)
  );
}

type CheckoutSuccessResponse = {
  success: boolean;
  data?: {
    organizationId: string;
    planId: string;
    planName: string;
    subscriptionId: string | null;
    subscriptionStatus: string | null;
    customerId: string | null;
    sessionId: string;
    created?: boolean;
    organizationName?: string | null;
  };
  error?: string;
};

export function BillingSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [planName, setPlanName] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [organizationCreated, setOrganizationCreated] = useState(false);
  const { user, updateUser, refreshUser, switchOrganization } = useAuth();
  const confirmationStatusRef = useRef<"idle" | "pending" | "success" | "error">(
    "idle",
  );
  const latestUserRef = useRef(user);
  const updateUserRef = useRef(updateUser);
  const refreshUserRef = useRef(refreshUser);
  const switchOrganizationRef = useRef(switchOrganization);
  const queryClient = useQueryClient();

  useEffect(() => {
    latestUserRef.current = user;
  }, [user]);

  useEffect(() => {
    updateUserRef.current = updateUser;
  }, [updateUser]);

  useEffect(() => {
    refreshUserRef.current = refreshUser;
  }, [refreshUser]);

  useEffect(() => {
    switchOrganizationRef.current = switchOrganization;
  }, [switchOrganization]);

  useEffect(() => {
    let isActive = true;

    const confirmCheckoutSession = async () => {
      if (!sessionId) {
        setError("No session ID found");
        setIsLoading(false);
        return;
      }

      // Check if this session has already been processed
      const processedKey = `billing_processed_${sessionId}`;
      const alreadyProcessed = sessionStorage.getItem(processedKey);
      
      if (alreadyProcessed === "true") {
        console.info("[BillingSuccess] session already processed, redirecting to dashboard");
        setIsLoading(false);
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 500);
        return;
      }

      if (confirmationStatusRef.current === "pending" || confirmationStatusRef.current === "success") {
        console.info("[BillingSuccess] confirmation already in progress or complete");
        return;
      }

      confirmationStatusRef.current = "pending";
      console.info("[BillingSuccess] starting confirmation process");

      try {
        await processCheckoutSession(isActive);
      } catch (error) {
        console.error("[BillingSuccess] fatal error in checkout process", error);
        throw error;
      } finally {
        // Absolutely ensure loading state is cleared
        if (isActive) {
          console.info("[BillingSuccess] clearing loading state");
          setIsLoading(false);
        }
      }
    };

    async function processCheckoutSession(isActive: boolean) {
      let storedOrganizationId: string | undefined;
      let storedPlanId: SupportedPlanId | undefined;
      let storedOrganizationData:
        | {
            name: string;
            description?: string;
            industry?: string;
            companySize?: string;
          }
        | undefined;

      let resolvedPlanName: string | null = null;

      try {
        const rawBilling = sessionStorage.getItem("pendingBilling");
        if (rawBilling) {
          const parsed = JSON.parse(rawBilling) as {
            organizationId?: string;
            planId?: string;
            organization?: {
              name?: string;
              description?: string;
              industry?: string;
              companySize?: string;
            };
          };
          if (
            parsed.organizationId &&
            typeof parsed.organizationId === "string"
          ) {
            storedOrganizationId = parsed.organizationId;
          }
          if (isSupportedPlanId(parsed.planId)) {
            storedPlanId = parsed.planId;
          }
          if (parsed.organization?.name) {
            storedOrganizationData = {
              name: parsed.organization.name,
              description: parsed.organization.description,
              industry: parsed.organization.industry,
              companySize: parsed.organization.companySize,
            };
          }
        }
      } catch (storageError) {
        console.warn("Failed to parse pending billing storage", storageError);
      }

      if (!storedOrganizationId) {
        try {
          const rawPendingOrg = sessionStorage.getItem("pendingOrganization");
          if (rawPendingOrg) {
            const parsed = JSON.parse(rawPendingOrg) as {
              organizationId?: string;
              organization_id?: string;
              plan?: string;
              name?: string;
              description?: string;
              industry?: string;
              companySize?: string;
            };
            const fallbackOrgId =
              parsed.organizationId || parsed.organization_id;
            if (fallbackOrgId && typeof fallbackOrgId === "string") {
              storedOrganizationId = fallbackOrgId;
            }
            if (!storedPlanId && isSupportedPlanId(parsed.plan)) {
              storedPlanId = parsed.plan;
            }
            if (parsed.name && !storedOrganizationData) {
              storedOrganizationData = {
                name: parsed.name,
                description: parsed.description,
                industry: parsed.industry,
                companySize: parsed.companySize,
              };
            }
          }
        } catch (storageError) {
          console.warn(
            "Failed to parse pending organization storage",
            storageError,
          );
        }
      }

      const payload: {
        sessionId: string;
        organizationId?: string;
        planId?: SupportedPlanId;
        organization?: {
          name: string;
          description?: string;
          industry?: string;
          companySize?: string;
        };
      } = { sessionId: sessionId! };

      if (storedOrganizationId) {
        payload.organizationId = storedOrganizationId;
      }

      if (storedPlanId) {
        payload.planId = storedPlanId;
      }

      if (!storedOrganizationId && storedOrganizationData) {
        payload.organization = storedOrganizationData;
        if (!storedPlanId) {
          console.warn(
            "Pending organization data provided without plan identifier; backend validation may fail.",
          );
        }
      }

      try {
        console.info("[BillingSuccess] confirming session", {
          sessionId,
          organizationId: payload.organizationId,
          hasOrganizationData: Boolean(payload.organization),
        });

        const response = await api.post<CheckoutSuccessResponse>(
          "/billing/checkout/success",
          payload,
        );

        console.info("[BillingSuccess] confirmation response", response);
        console.info("[BillingSuccess] response.data details", {
          success: response.success,
          hasData: !!response.data,
          data: response.data,
        });

        if (!response.success || !response.data) {
          const message =
            response.error ?? "Payment captured but confirmation failed";
          console.error("[BillingSuccess] response validation failed", { response });
          throw new Error(message);
        }

        console.info("[BillingSuccess] response validated, proceeding...");

        if (!isActive) {
          console.warn("[BillingSuccess] component no longer active, aborting");
          return;
        }

        confirmationStatusRef.current = "success";
        console.info("[BillingSuccess] status set to success");

        const friendlyPlanName = response.data.planName || response.data.planId;
        resolvedPlanName = friendlyPlanName;
        
        console.info("[BillingSuccess] setting UI state...", {
          planName: friendlyPlanName,
          organizationName: response.data.organizationName ?? storedOrganizationData?.name,
          created: response.data.created,
        });
        
        setPlanName(friendlyPlanName);
        setOrganizationName(
          response.data.organizationName ??
            storedOrganizationData?.name ??
            null,
        );
        setOrganizationCreated(Boolean(response.data.created));

        console.info("[BillingSuccess] UI state set successfully");

        const currentUser = latestUserRef.current;
        const applyUpdate = updateUserRef.current;
        
        console.info("[BillingSuccess] auth context check", {
          hasCurrentUser: !!currentUser,
          hasApplyUpdate: !!applyUpdate,
          currentUserId: currentUser?.id,
        });

        if (currentUser && applyUpdate) {
          const rawPlanId = isSupportedPlanId(response.data.planId)
            ? response.data.planId
            : "STARTER";

          const mappedPlan: Organization['plan'] = rawPlanId === "ENTERPRISE"
            ? "enterprise"
            : rawPlanId === "PROFESSIONAL"
              ? "professional"
              : "professional";

          const existingOrganizations = Array.isArray(currentUser.organizations)
            ? currentUser.organizations
            : [];

          const orgId = response.data.organizationId;

          const organizationExists = existingOrganizations.some(
            (org) => org.id === orgId,
          );

          const enrichedOrganizationName =
            response.data.organizationName ??
            storedOrganizationData?.name ??
            existingOrganizations.find((org) => org.id === orgId)?.name ??
            "Organization";

          const nextOrganizations = organizationExists
            ? existingOrganizations.map((org) =>
                org.id === orgId
                  ? {
                      ...org,
                      name: enrichedOrganizationName,
                      plan: mappedPlan,
                      role: org.role ?? "owner",
                    }
                  : org,
              )
            : [
                ...existingOrganizations,
                {
                  id: orgId,
                  name: enrichedOrganizationName,
                  plan: mappedPlan,
                  role: "owner" as const,
                  industry: storedOrganizationData?.industry ?? undefined,
                  companySize: storedOrganizationData?.companySize ?? undefined,
                  description: storedOrganizationData?.description ?? undefined,
                  timeZone: undefined,
                  avatar: undefined,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                },
              ];

          applyUpdate({
            organizations: nextOrganizations,
            currentOrganizationId: orgId,
            organizationRole: "owner",
          });

          console.info("[BillingSuccess] local auth updated", {
            currentOrganizationId: orgId,
            organizations: nextOrganizations,
          });
        }

        sessionStorage.removeItem("pendingOrganization");
        sessionStorage.removeItem("pendingBilling");

        // sync the auth context with the server to ensure any other fields
        // (e.g. permissions, defaults) are refreshed for the new org
        const targetOrgId = response.data.organizationId;
        
        // Execute backend operations
        let operationsSucceeded = false;
        try {
          // First, switch organization on the backend
          if (targetOrgId) {
            console.info("[BillingSuccess] switching organization on backend", targetOrgId);
            await switchOrganizationRef.current?.(targetOrgId);
            console.info("[BillingSuccess] switchOrganization complete", targetOrgId);
          }

          // Then refresh user to get the latest state from server
          const refreshed = await refreshUserRef.current?.();
          console.info("[BillingSuccess] refreshed user", refreshed);

          // Invalidate all queries to force refetch
          await queryClient.invalidateQueries();
          
          operationsSucceeded = true;
          console.info("[BillingSuccess] all operations complete, will navigate to dashboard");
          
        } catch (operationError) {
          console.error("[BillingSuccess] failed to complete post-payment operations", operationError);
          // Still allow navigation even if some operations failed
        }
        
        // Mark session as processed to prevent duplicate processing
        const processedKey = `billing_processed_${sessionId}`;
        sessionStorage.setItem(processedKey, "true");
        
        // Navigate to dashboard after showing success message
        setTimeout(() => {
          if (!isActive) {
            console.warn("[BillingSuccess] component unmounted, skipping navigation");
            return;
          }
          console.info("[BillingSuccess] navigating to dashboard now");
          navigate('/dashboard', { replace: true });
        }, operationsSucceeded ? 1500 : 500);
      } catch (caughtError) {
        if (!isActive) return;

        console.error("Failed to confirm Stripe checkout session", caughtError);

        let fallback =
          "We could not confirm your payment. Please contact support with your receipt.";

        if (caughtError instanceof ApiError) {
          try {
            const parsed = JSON.parse(caughtError.message) as {
              error?: string;
            };
            if (parsed?.error) {
              fallback = parsed.error;
            }
          } catch {
            if (caughtError.message) {
              fallback = caughtError.message;
            }
          }
        } else if (caughtError instanceof Error && caughtError.message) {
          fallback = caughtError.message;
        }

        setError(fallback);
        confirmationStatusRef.current = "error";
      }
    }

    void confirmCheckoutSession();

    return () => {
      isActive = false;
    };
    // we intentionally only depend on the session id so the confirmation is
    // attempted once per Stripe redirect. updates to auth context after the
    // success response should not re-trigger the flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mb-4"></div>
            <p className="text-muted-foreground">Processing your payment...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">Payment Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button
              onClick={() => navigate("/organization")}
              className="w-full"
            >
              Return to Settings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="flex-shrink-0 w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">
            Payment Successful!
          </CardTitle>
          <CardDescription className="text-lg">
            {planName
              ? `Your ${planName} subscription is active and ready to use.`
              : "Your subscription has been activated successfully."}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {organizationCreated && organizationName && (
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-md px-4 py-3 text-sm">
              {`A new organization named "${organizationName}" has been created and linked to your account.`}
            </div>
          )}

          <div className="bg-muted/50 rounded-lg p-6 text-center">
            <h3 className="font-semibold text-foreground mb-2">
              What happens next?
            </h3>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li>
                ✓ Your plan has been upgraded immediately
                {planName ? ` to ${planName}` : ""}
              </li>
              <li>✓ You now have access to all premium features</li>
              <li>✓ A confirmation email has been sent to your inbox</li>
              <li>
                ✓ You can manage your billing anytime in organization settings
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 rounded-md px-4 py-3 text-center">
            <div className="flex items-center justify-center gap-3">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
              <span className="text-sm font-medium">Redirecting to your dashboard...</span>
            </div>
          </div>

          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              Need help?{" "}
              <a
                href="mailto:support@gathio.com"
                className="text-primary hover:underline"
              >
                Contact our support team
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
